import {DragEndEvent} from '@dnd-kit/core';
import {useCallback} from 'react';

import {useViewAction} from '../../state/views/hooks';
import * as Actions from '../../state/views/panelBankConfig/actions';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {PanelDropRef} from './types';

export const useHandleDragEnd = (
  panelDropRef: React.RefObject<PanelDropRef | null>
) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const moveSection = useViewAction(panelBankConfigRef, Actions.moveSection);
  const movePanel = useViewAction(panelBankConfigRef, Actions.movePanel2);
  const movePanelToDiffSection = useViewAction(
    panelBankConfigRef,
    Actions.movePanelToDiffSection
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;

      // Only proceed if we have both active and over, and they're different
      if (!over || active.id === over.id) {
        return;
      }

      const dragType = active.data.current?.type;

      if (dragType === 'section') {
        const activeSectionRefId = active.id.toString();
        const targetSectionRefId = over.id.toString();
        moveSection(activeSectionRefId, targetSectionRefId);
        return;
      }

      if (dragType === 'panel') {
        const activeData = active.data.current;
        const targetData = over.data.current;

        const isSameSection =
          activeData?.sectionRef?.id === targetData?.sectionRef?.id;

        // This handles moving a panel within the same section
        // We need to have both panelsRefs in order to perform the action
        if (isSameSection && activeData?.panelRef && targetData?.panelRef) {
          movePanel(
            {
              panelRef: activeData.panelRef,
              sectionRef: activeData.sectionRef,
            },
            {
              panelRef: targetData.panelRef,
              sectionRef: targetData.sectionRef,
            }
          );
          return;
        }

        // This handles moving a panel to a different section. The target panelRef can be empty.
        // We cannot rely on the "over" type to determine where a panel should be inserted.
        // We use panelDropRef to determine the position more granularly and handle edge cases
        // like inserting into an empty section, beginning of a section, or end of a section.
        if (
          panelDropRef.current &&
          activeData?.panelRef &&
          targetData?.sectionRef
        ) {
          movePanelToDiffSection(
            {
              panelRef: activeData.panelRef,
              sectionRef: activeData.sectionRef,
            },
            {
              panelRef: panelDropRef.current.panelRef || null,
              sectionRef: panelDropRef.current.sectionRef,
              insertDir: panelDropRef.current.insertDir,
            }
          );
        }
      }
    },
    [movePanel, movePanelToDiffSection, moveSection, panelDropRef]
  );

  return handleDragEnd;
};
